import { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StarRow({ rating, size = 16, interactive = false, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Sterne-Bewertung">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || rating) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={!interactive}
            data-testid={interactive ? `review-star-${n}` : undefined}
            onClick={() => interactive && onChange?.(n)}
            onMouseEnter={() => interactive && setHover(n)}
            onMouseLeave={() => interactive && setHover(0)}
            className={
              interactive
                ? "cursor-pointer transition-transform hover:scale-110"
                : "cursor-default"
            }
            aria-label={`${n} Sterne`}
          >
            <Star
              size={size}
              strokeWidth={1.5}
              className={filled ? "fill-[#1a1918] text-[#1a1918]" : "text-[#c8c2bb]"}
            />
          </button>
        );
      })}
    </div>
  );
}

function formatDate(d) {
  try {
    const date = typeof d === "string" ? new Date(d) : d;
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "" });

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data || []);
    } catch (e) {
      console.error("Failed to load reviews", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.comment.trim()) {
      toast.error("Bitte fülle alle Felder aus.");
      return;
    }
    if (form.comment.trim().length < 3) {
      toast.error("Dein Kommentar ist zu kurz.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        name: form.name.trim(),
        rating: Number(form.rating),
        comment: form.comment.trim(),
      });
      // Refetch the public list so only published reviews are shown
      await fetchReviews();
      setForm({ name: "", rating: 5, comment: "" });
      if (form.rating >= 4) {
        toast.success("Danke für deine Bewertung!");
      } else {
        toast.success(
          "Danke für dein Feedback! Wir werden es uns zu Herzen nehmen."
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Bewertung konnte nicht gesendet werden.");
    } finally {
      setSubmitting(false);
    }
  };

  const avg =
    reviews.length > 0
      ? (
          reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length
        ).toFixed(1)
      : "5,0";

  return (
    <section
      id="bewertungen"
      data-testid="reviews-section"
      className="section-pad bg-cream"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 mb-16">
          <div className="md:col-span-7">
            <span className="eyebrow">Bewertungen</span>
            <h2 className="font-display mt-4 text-5xl font-bold leading-[0.95] tracking-tight text-brand md:text-6xl">
              Was unsere Gäste sagen.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex md:justify-end md:items-end">
            <div className="text-left md:text-right">
              {reviews.length > 0 ? (
                <>
                  <p
                    data-testid="reviews-average"
                    className="font-display text-6xl font-bold text-brand"
                  >
                    {String(avg).replace(".", ",")}
                  </p>
                  <div className="mt-2 md:flex md:justify-end">
                    <StarRow
                      rating={Math.round(parseFloat(avg))}
                      size={18}
                    />
                  </div>
                  <p className="mt-2 text-sm text-[#5c5752]">
                    {reviews.length}{" "}
                    {reviews.length === 1 ? "Bewertung" : "Bewertungen"}
                  </p>
                </>
              ) : (
                <p className="text-sm text-[#5c5752] md:max-w-[260px]">
                  Noch keine Bewertungen – sei der oder die Erste.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Reviews grid */}
        <div
          data-testid="reviews-list"
          className={
            reviews.length > 0
              ? "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
              : ""
          }
        >
          {loading && (
            <p className="text-[#5c5752]">Lade Bewertungen…</p>
          )}
          {!loading &&
            reviews.slice(0, 6).map((r, idx) => (
              <article
                key={r.id || idx}
                data-testid={`review-card-${idx}`}
                className="flex h-full flex-col rounded-3xl border border-warm bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand"
              >
                <StarRow rating={r.rating} size={16} />
                <p className="mt-5 flex-1 text-base leading-relaxed text-brand">
                  „{r.comment}"
                </p>
                <div className="mt-6 flex items-center justify-between border-t border-warm pt-4">
                  <p className="font-display text-sm font-semibold text-brand">
                    {r.name}
                  </p>
                  <p className="text-xs uppercase tracking-wider text-[#5c5752]">
                    {formatDate(r.created_at)}
                  </p>
                </div>
              </article>
            ))}
        </div>

        {/* Submit form */}
        <div className="mt-20 grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="eyebrow">Schreibe uns</span>
            <h3 className="font-display mt-4 text-3xl font-bold text-brand md:text-4xl">
              Hinterlasse deine Bewertung.
            </h3>
            <p className="mt-4 text-[#5c5752]">
              Hat dir dein Besuch gefallen? Wir freuen uns über jedes Feedback –
              direkt hier auf der Seite.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="review-form"
            className="md:col-span-7 grid grid-cols-1 gap-5 rounded-3xl border border-warm bg-white p-8"
          >
            <div>
              <Label
                htmlFor="review-name"
                className="text-xs uppercase tracking-[0.2em] text-[#5c5752]"
              >
                Dein Name
              </Label>
              <Input
                id="review-name"
                data-testid="review-input-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Max Mustermann"
                className="mt-2 border-warm bg-white"
                maxLength={80}
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#5c5752]">
                Deine Sterne
              </Label>
              <div className="mt-3">
                <StarRow
                  rating={form.rating}
                  size={28}
                  interactive
                  onChange={(n) => setForm({ ...form, rating: n })}
                />
              </div>
            </div>

            <div>
              <Label
                htmlFor="review-comment"
                className="text-xs uppercase tracking-[0.2em] text-[#5c5752]"
              >
                Dein Kommentar
              </Label>
              <Textarea
                id="review-comment"
                data-testid="review-input-comment"
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                placeholder="Was hat dir besonders gefallen?"
                className="mt-2 min-h-[120px] border-warm bg-white"
                maxLength={1000}
              />
            </div>

            <button
              type="submit"
              data-testid="review-submit-button"
              disabled={submitting}
              className="btn-primary w-full md:w-auto md:self-start disabled:opacity-60"
            >
              {submitting ? "Wird gesendet…" : "Bewertung senden"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
